<template>
  <!-- Chat del asistente (contrato § 11) + borradores dentro del hilo (§ 2–8).
       Genérico: el módulo de contexto llega por prop. -->
  <div
    class="ac"
    @dragover.prevent="dragging = canUpload"
    @dragleave.self="dragging = false"
    @drop.prevent="onDrop"
  >
    <div ref="threadRef" class="ac-thread" aria-live="polite">
      <!-- Bienvenida -->
      <div class="ac-msg ac-msg--bot">
        <span class="ac-avatar" aria-hidden="true"><PhSparkle :size="14" weight="fill" /></span>
        <div class="ac-bubble">
          <p class="ac-p">{{ canUpload ? $t('assistant.chat.welcome') : $t('assistant.chat.welcomeChatOnly') }}</p>
          <div v-if="!items.length" class="ac-chips">
            <button v-if="canUpload" type="button" class="ac-chip" @click="pickFile">
              <PhPaperclip :size="14" /> {{ $t('assistant.chat.starterUpload') }}
            </button>
            <button v-for="s in starters" :key="s" type="button" class="ac-chip" @click="send(s)">{{ s }}</button>
            <button v-if="pendingCount" type="button" class="ac-chip" @click="$emit('show-pending')">
              {{ $t('assistant.chat.starterPending', { n: pendingCount }) }}
            </button>
          </div>
        </div>
      </div>

      <template v-for="m in items" :key="m.id">
        <div v-if="m.type === 'separator'" class="ac-sep" role="separator"><span>{{ m.content }}</span></div>

        <div v-else-if="m.role === 'user'" class="ac-msg ac-msg--user">
          <div class="ac-bubble ac-bubble--user">
            <template v-if="m.type === 'file'"><PhFileText :size="16" /> <span class="ac-file" :title="m.content">{{ shortFileName(m.content, 32) }}</span></template>
            <template v-else>{{ m.content }}</template>
          </div>
        </div>

        <div v-else-if="m.type === 'draft'" class="ac-msg ac-msg--card">
          <DraftCard
            :initial-draft="m.draft"
            :read-only="readOnly"
            @changed="$emit('drafts-changed')"
            @navigate="(to) => $emit('navigate', to)"
          />
        </div>

        <div v-else class="ac-msg ac-msg--bot">
          <span class="ac-avatar" aria-hidden="true"><PhSparkle :size="14" weight="fill" /></span>
          <div class="ac-bubble" :class="{ 'ac-bubble--error': m.type === 'error' }">
            <span v-if="m.type === 'typing'" class="ac-typing" :aria-label="m.content">
              <i></i><i></i><i></i><span>{{ m.content }}</span>
            </span>
            <template v-else>
              <!-- renderMarkdown sanitiza con DOMPurify -->
              <div v-if="m.type === 'text'" class="ac-md" v-safe-html="renderMarkdown(m.content)"></div>
              <p v-else class="ac-p">{{ m.content }}</p>
              <div v-if="actionButton(m)" class="ac-actions">
                <DsButton size="sm" variant="primary" @click="runAction(m.action)">{{ actionButton(m) }}</DsButton>
                <span v-if="m.action === 'contact_support'" class="ac-support">{{ SUPPORT_EMAIL }}</span>
              </div>
              <div v-if="m.followups?.length && isLastBot(m)" class="ac-chips">
                <button v-for="f in m.followups" :key="f" type="button" class="ac-chip" :disabled="sending" @click="send(f)">{{ f }}</button>
              </div>
              <div v-if="m.retryFile" class="ac-chips">
                <button type="button" class="ac-chip" @click="pickFile">{{ $t('assistant.chat.tryAnother') }}</button>
              </div>
            </template>
          </div>
        </div>
      </template>
    </div>

    <!-- Escribir / adjuntar -->
    <form ref="composerRef" class="ac-composer" @submit.prevent="send()">
      <input ref="fileRef" type="file" class="ac-hidden" :accept="DOCUMENT_ACCEPT" @change="onPick" />
      <button
        v-if="canUpload"
        type="button"
        class="ac-icon-btn"
        :title="$t('assistant.chat.attach')"
        :aria-label="$t('assistant.chat.attach')"
        :disabled="uploading"
        @click="pickFile"
      >
        <PhPaperclip :size="20" />
      </button>
      <DsTextarea
        v-model="text"
        class="ac-input"
        rows="1"
        fixed
        :maxlength="CHAT_MAX_LENGTH"
        :placeholder="$t('assistant.chat.placeholder')"
        :aria-label="$t('assistant.chat.placeholder')"
        @keydown.enter.exact.prevent="send()"
        @input="autosize"
      />
      <button
        type="submit"
        class="ac-send"
        :disabled="!text.trim() || sending"
        :title="$t('assistant.chat.send')"
        :aria-label="$t('assistant.chat.send')"
      >
        <PhPaperPlaneRight :size="18" weight="fill" />
      </button>
    </form>
    <p v-if="canUpload" class="ac-formats">{{ $t('assistant.chat.formats') }}</p>

    <div v-if="dragging" class="ac-drop" @dragleave="dragging = false">
      <PhUploadSimple :size="32" /><span>{{ $t('assistant.chat.dropHere') }}</span>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, nextTick, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { PhSparkle, PhPaperclip, PhFileText, PhPaperPlaneRight, PhUploadSimple } from '@phosphor-icons/vue';
import DsButton from '@/components/ds/DsButton.vue';
import DsTextarea from '@/components/ds/DsTextarea.vue';
import DraftCard from './DraftCard.vue';
import { useUserStore } from '@/store/userStore';
import { renderMarkdown } from '@/utils/renderMarkdown';
import { shortFileName } from '@/utils/shortFileName';
import { SUPPORT_EMAIL } from '@/config/contact';
import {
  useAssistant, draftOf, DOCUMENT_ACCEPT, DOCUMENT_EXT, SPREADSHEET_EXT, DOCUMENT_MAX_IMAGE, DOCUMENT_MAX_FILE, CHAT_MAX_LENGTH,
} from '@/composables/useAssistant';
let seq = 0;
const uid = () => `a${Date.now()}${seq++}`;

export default {
  name: 'AssistantChat',
  components: { DsButton, DsTextarea, DraftCard, PhSparkle, PhPaperclip, PhFileText, PhPaperPlaneRight, PhUploadSimple },
  props: {
    contextModule: { type: String, default: null },   // pantalla desde la que se pregunta (solo contexto)
    uploadModule: { type: String, default: null },    // a qué módulo va un documento adjunto
    canUpload: { type: Boolean, default: false },     // plan + rol permiten subir
    readOnly: { type: Boolean, default: false },      // Auditor: ve borradores, no actúa
    pendingCount: { type: Number, default: 0 },
    intent: { type: Object, default: null },          // { attach?, draftId? }
  },
  emits: ['show-pending', 'drafts-changed', 'navigate'],
  setup(props, { emit, expose }) {
    const { t, tm, rt } = useI18n();
    const api = useAssistant();
    const { user } = storeToRefs(useUserStore());

    const threadRef = ref(null);
    const fileRef = ref(null);
    const composerRef = ref(null);
    const items = ref([]);
    const text = ref('');
    const sending = ref(false);
    const uploading = ref(false);
    const dragging = ref(false);

    // Conversación: se recuerda por usuario para retomarla al volver.
    const storeKey = computed(() => `gme.assistant.conversation.${user.value?.id || 'anon'}`);
    const conversationId = ref(null);
    const saveConversation = () => { try { localStorage.setItem(storeKey.value, String(conversationId.value)); } catch { /* storage bloqueado */ } };

    const starters = computed(() => { try { return tm('assistant.chat.starters').map(s => rt(s)); } catch { return []; } });

    const scrollDown = async () => { await nextTick(); const el = threadRef.value; if (el) el.scrollTop = el.scrollHeight; };
    const add = (m) => { items.value.push({ id: uid(), ...m }); scrollDown(); };
    const typing = (content) => {
      const id = uid();
      items.value.push({ id, role: 'assistant', type: 'typing', content });
      scrollDown();
      return () => { items.value = items.value.filter(x => x.id !== id); };
    };
    const isLastBot = (m) => [...items.value].reverse().find(x => x.role !== 'user' && x.type === 'text')?.id === m.id;

    // DsTextarea arranca con un comentario, así que su `$el` no es el <textarea>:
    // se busca dentro de la barra.
    const inputEl = () => composerRef.value?.querySelector('textarea') || null;
    const autosize = () => {
      const el = inputEl();
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = `${Math.min(el.scrollHeight, 62)}px`;  // = max-height del CSS
    };

    // ── Chat ────────────────────────────────────────────────────────
    const send = async (preset) => {
      const message = String(preset ?? text.value).trim();
      if (!message || sending.value) return;
      if (preset === undefined) { text.value = ''; nextTick(autosize); }
      add({ role: 'user', type: 'text', content: message });
      sending.value = true;
      const stop = typing(t('assistant.chat.typing'));
      try {
        let res = await api.sendChat({ message, module: props.contextModule, conversationId: conversationId.value });
        if (!res.ok && res.code === 'CONVERSATION_NOT_FOUND') {
          conversationId.value = null;
          res = await api.sendChat({ message, module: props.contextModule });
        }
        stop();
        if (res.ok) {
          // Caducó (24 h sin actividad): el backend abrió otra. Separador antes de la respuesta.
          if (res.data?.restarted) {
            const at = items.value.length - 1; // antes del mensaje que se acaba de enviar
            items.value.splice(Math.max(at, 0), 0, { id: uid(), type: 'separator', content: t('assistant.chat.newConversation') });
          }
          if (res.data?.conversationId) { conversationId.value = res.data.conversationId; saveConversation(); }
          const msg = res.data?.message || {};
          add({ role: 'assistant', type: 'text', content: msg.content || '', action: msg.action || null, followups: msg.followups || [] });
        } else {
          add({ role: 'assistant', type: 'error', content: res.message || t('assistant.errors.generic') });
        }
      } catch {
        stop();
        add({ role: 'assistant', type: 'error', content: t('assistant.errors.generic') });
      } finally {
        sending.value = false;
      }
    };

    const actionButton = (m) => {
      if (m.action === 'upload_document') return props.canUpload ? t('assistant.chat.actions.upload') : null;
      if (m.action === 'open_pending_drafts') return t('assistant.chat.actions.pending');
      if (m.action === 'contact_support') return t('assistant.chat.actions.support');
      return null;
    };
    const runAction = (action) => {
      if (action === 'upload_document') pickFile();
      if (action === 'open_pending_drafts') emit('show-pending');
      if (action === 'contact_support') window.location.href = `mailto:${SUPPORT_EMAIL}`;
    };

    // ── Documentos (nunca van a /chat: van a /drafts, § 11) ─────────
    const pickFile = () => { if (props.canUpload) fileRef.value?.click(); };
    const validate = (file) => {
      if (/\.xls$/i.test(file.name)) return t('assistant.errors.xls');
      if (!DOCUMENT_EXT.test(file.name)) return t('assistant.errors.type');
      if (/^image\//.test(file.type) && file.size > DOCUMENT_MAX_IMAGE) return t('assistant.errors.imageSize');
      if (file.size > DOCUMENT_MAX_FILE) return t('assistant.errors.fileSize');
      return null;
    };
    const upload = async (file) => {
      if (!file || uploading.value || !props.canUpload || !props.uploadModule) return;
      add({ role: 'user', type: 'file', content: file.name });
      const bad = validate(file);
      if (bad) { add({ role: 'assistant', type: 'error', content: bad }); return; }
      uploading.value = true;
      const readingKey = SPREADSHEET_EXT.test(file.name) ? 'assistant.chat.readingSheet'
        : /\.xml$/i.test(file.name) ? 'assistant.chat.readingXml' : 'assistant.chat.reading';
      const stop = typing(t(readingKey));
      try {
        const res = await api.createDraft(file, props.uploadModule);
        stop();
        if (res.ok) {
          add({ role: 'assistant', type: 'draft', draft: draftOf(res) });
          emit('drafts-changed');
        } else {
          add({ role: 'assistant', type: 'error', content: res.message || t('assistant.errors.generic'), retryFile: res.code === 'EXTRACTION_FAILED' });
        }
      } catch {
        stop();
        add({ role: 'assistant', type: 'error', content: t('assistant.errors.generic') });
      } finally {
        uploading.value = false;
      }
    };
    const onPick = (e) => { const f = e.target.files?.[0]; e.target.value = ''; upload(f); };
    const onDrop = (e) => { dragging.value = false; upload(e.dataTransfer?.files?.[0]); };

    // Retomar un borrador (desde "Por revisar").
    const openDraft = async (id) => {
      const stop = typing(t('assistant.thinking'));
      const res = await api.getDraft(id);
      stop();
      if (res.ok) add({ role: 'assistant', type: 'draft', draft: draftOf(res) });
      else add({ role: 'assistant', type: 'error', content: res.message || t('assistant.errors.generic') });
    };

    const applyIntent = async (i) => {
      if (!i) return;
      if (i.attach) { await nextTick(); pickFile(); }
      if (i.draftId) openDraft(i.draftId);
    };
    watch(() => props.intent, applyIntent);

    // ── Historial ───────────────────────────────────────────────────
    onMounted(async () => {
      let saved = null;
      try { saved = localStorage.getItem(storeKey.value); } catch { /* storage bloqueado */ }
      if (saved && saved !== 'null') {
        const res = await api.getConversation(saved);
        const list = res.data?.messages || res.data?.conversation?.messages || [];
        if (res.ok && list.length) {
          conversationId.value = res.data?.conversationId || res.data?.conversation?.id || saved;
          list.forEach((m) => add({
            role: m.role === 'user' ? 'user' : 'assistant', type: 'text',
            content: m.content, action: m.action || null, followups: m.followups || [],
          }));
        } else {
          try { localStorage.removeItem(storeKey.value); } catch { /* storage bloqueado */ }
        }
      }
      await applyIntent(props.intent);
      scrollDown();
    });

    const focus = () => { inputEl()?.focus(); scrollDown(); };

    // "Nueva conversación": el próximo mensaje va sin conversationId (§ 11).
    const newConversation = () => {
      conversationId.value = null;
      try { localStorage.removeItem(storeKey.value); } catch { /* storage bloqueado */ }
      if (items.value.length) add({ type: 'separator', content: t('assistant.chat.newConversation') });
      focus();
    };
    expose({ openDraft, pickFile, focus, newConversation });

    return {
      DOCUMENT_ACCEPT, CHAT_MAX_LENGTH, SUPPORT_EMAIL,
      threadRef, fileRef, composerRef, items, text, sending, uploading, dragging, starters,
      renderMarkdown, shortFileName, isLastBot, autosize, send, actionButton, runAction, pickFile, onPick, onDrop,
    };
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/components/assistant/assistant-chat";
</style>
